import React from 'react';
import { defaultMemoize } from 'reselect';

import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { CollectionsSubspaceName, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../codegen/Localize';
import { CollectionDefinition } from '../contentDefinitions/CollectionDefinition';
import { NewCollectionButton } from './NewCollectionButton';

export interface AllCollectionsSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
}

export interface AllCollectionsSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly isBusyFetchingItems: boolean;
  readonly numberOfFetchedCollections: number;
  readonly userDisplayName: string;
}

export type AllCollectionsSpaceProps = AllCollectionsSpaceOwnProps & AllCollectionsSpaceStateProps;

export class AllCollectionsSpace extends React.PureComponent<AllCollectionsSpaceProps> {
  private memoizedGetSubspaces = defaultMemoize(getSubspaces);

  private static siteUrlBySubspace: Record<CollectionsSubspaceName, (urls: SpacesSiteUrls) => string> = {
    all: (urls) => urls.allCollectionsUrl(),
    my: (urls) => urls.myCollectionsUrl()
  };

  public getCollectionsSubspaceUrl = (subspaceName?: CollectionsSubspaceName) => {
    let url = this.props.urls.tasksUrl();
    if (subspaceName) {
      const urlFn = AllCollectionsSpace.siteUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls) || url;
    }
    return url;
  };

  public render(): JSX.Element {
    const subspaces = this.memoizedGetSubspaces(this.props.urls, this.props.userDisplayName);
    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.getCollectionsSubspaceUrl}
        mastheadElement={this.masthead()}
        testIdPrefix={'collections'}
        spaceItemName={Messages.collectionsSpaceLabel()}
      />
    );
  }

  private masthead = (): JSX.Element => {
    return <SpaceHeaderContent headerText={Messages.collectionsSpaceLabel()} />;
  };
}

function getSubspaces(urls: SpacesSiteUrls, userDisplayName: string): ReadonlyArray<Subspace> {
  return [
    explorerSubspace({
      name: 'all',
      tabLabel: Messages.collectionsAllTab(),
      supportedDisplayModes: CollectionDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      toolbarVisibility: 'enabled',
      supportFilterPanel: true,
      toolbarContentSpecificControls: { beginning: createNewCollectionButton(urls, userDisplayName) },
      emptyStateProps: {
        primaryMessage: Messages.collectionsEmptyMessage()
      }
    }),
    explorerSubspace({
      name: 'my',
      tabLabel: Messages.collectionsMyTab(),
      supportedDisplayModes: CollectionDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      toolbarVisibility: 'enabled',
      supportFilterPanel: true,
      toolbarContentSpecificControls: { beginning: createNewCollectionButton(urls, userDisplayName) },
      emptyStateProps: {
        primaryMessage: Messages.collectionsEmptyMessage()
      }
    })
  ];
}

function createNewCollectionButton(urls: SpacesSiteUrls, userDisplayName: string): JSX.Element {
  return (<NewCollectionButton
    urls={urls}
    userDisplayName={userDisplayName} />);
}
